import { LscApiError } from './api';
import { compactLeagueId } from './adminIssueCopy';
import { buildInternalCopyPattern } from './internalSanitizerPatterns';
import type { LscAdminLeagueSummaryIdentity } from './types';

export const LSC_ADMIN_LEAGUE_SUMMARY_TITLE = 'League health summary';
export const LSC_ADMIN_LEAGUE_SUMMARY_INTRO =
  'Read-only launch health for one league. Composed from billing, readiness, automation, schedule, workflow, and support issue read models.';
export const LSC_ADMIN_LEAGUE_SUMMARY_NAVIGATION_NOTE =
  'Setup progress shown here is navigation context only and is not product truth.';
export const LSC_ADMIN_LEAGUE_SUMMARY_ARCHIVE_NOTE =
  'Archive autopublish remains stage-gated and non-actionable from admin.';
export const LSC_ADMIN_LEAGUE_SUMMARY_NOT_FOUND = 'League not found or unavailable.';
export const LSC_ADMIN_LEAGUE_SUMMARY_ISSUES_EMPTY = 'No support issues reported for this league yet.';

const INTERNAL_COPY_PATTERN = buildInternalCopyPattern(
  'firestore|cloud run|webhook|stripe payload|oauth|api_key|secret|token|operator|env_|__ENV_PREFIX__|raw payload|session cookie|allowlist|cus_|sub_'
);

export function parseAdminLeagueSummaryError(err: unknown, fallback?: string): string {
  if (err instanceof LscApiError) {
    if (err.status === 403 || err.code === 'admin_forbidden') {
      return 'You do not have access to LSC admin tools.';
    }
    if (err.code === 'league_not_found' || err.status === 404) {
      return LSC_ADMIN_LEAGUE_SUMMARY_NOT_FOUND;
    }
    if (err.message && !INTERNAL_COPY_PATTERN.test(err.message)) {
      return err.message;
    }
  }
  if (err instanceof Error && err.message && !INTERNAL_COPY_PATTERN.test(err.message)) {
    return err.message;
  }
  return fallback ?? 'Unable to load league health summary.';
}

export function formatAdminSummaryValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

export function adminLeagueSummaryIdentityLine(
  input: Pick<LscAdminLeagueSummaryIdentity, 'league_id' | 'league_name'>
): string {
  const name = String(input.league_name || '').trim() || compactLeagueId(input.league_id);
  const id = compactLeagueId(input.league_id);
  return id && name !== id ? `${name} (${id})` : name;
}

export const ADMIN_LEAGUE_SUMMARY_FORBIDDEN_CONTROL_LABELS = [
  'Firestore',
  'Cloud Run',
  'Stripe subscription',
  'Scheduler',
  'Cloud Tasks',
  'Force schedule apply',
  'VOD backfill',
  'Ownership transfer',
  'Impersonate',
  'Env editor',
  'Enable autopublish',
  'Controlled apply',
] as const;

export function assertAdminLeagueSummaryCopySafe(text: string): boolean {
  return !INTERNAL_COPY_PATTERN.test(text);
}
